import { Delta } from 'quill/core';

import { isDeltaBlank } from 'src/app/job/job-activity/comments.utils';

import { TransactionStage, TransactionType } from 'src/generated/graphql.generated';

import {
    PaymentTypeConfig,
    PaymentTypeUnavailable,
    CreateTransactionMethod,
    CreateTransactionStep,
    CreateTransactionTarget,
    CREATE_TRANSACTION_STEPS,
} from './create-transaction-dialog.state';

// Requirement: this must stay exactly the contract's value — the middleware
// and the refund dialog both branch on it, and `terminal` (Connect-origin,
// partial refunds) versus `terminal_standalone` (device-origin, full refunds
// only) is a distinction that has to stay correct.
export const TERMINAL_CHANNEL = 'terminal';

export function decideFiling(input: {
    depositSelected: boolean;
    invoiceId: string | undefined;
}): TransactionType {
    if (input.depositSelected) { return TransactionType.Deposit; }
    if (input.invoiceId) { return TransactionType.Payment; }

    return TransactionType.Deposit;
}

export function filingVariables(targets: {
    depositSelected: boolean;
    invoiceId: string | undefined;
    newInvoiceEventIds: string[] | undefined;
}): { type: TransactionType; invoiceId?: string; newInvoiceEventIds?: string[] } {
    if (targets.newInvoiceEventIds) {
        if (!targets.newInvoiceEventIds.length) {
            throw new Error('The events chosen for this invoice can no longer be invoiced. '
                + 'Go back and choose what you are collecting again.');
        }

        return { type: TransactionType.Payment, newInvoiceEventIds: targets.newInvoiceEventIds };
    }

    return {
        type: decideFiling(targets),
        // Requirement: money collected against an invoice is posted against that
        // invoice, whichever way it files — an invoice's `paidTotal` counts every paid
        // transaction linked to it regardless of type.
        ...(targets.invoiceId ? { invoiceId: targets.invoiceId } : {}),
    };
}

export function predictAutoAppliedMinor(input: {
    invoiceTotalMinor: number;
    currency: string;
    jobHasLiveInvoice: boolean;
    transactions: {
        deletedAt?: number | null;
        archivedAt?: number | null;
        stage?: TransactionStage | null;
        amount?: number | null;
        currency?: string | null;
    }[];
}): number {
    if (input.jobHasLiveInvoice) { return 0; }

    const paid = input.transactions.filter((t) => !t.deletedAt && t.stage === TransactionStage.Paid);

    if (paid.some((t) => t.archivedAt || t.currency !== input.currency)) { return 0; }

    const sum = paid.reduce((acc, t) => acc + (t.amount ?? 0), 0);

    return sum < 0 || sum > input.invoiceTotalMinor ? 0 : sum;
}

// Requirement: step one is skipped only when there is no real choice — zero
// open invoices defaults to a deposit, exactly one to a payment on it; two or
// more, an open authorization, or work that can be invoiced now routes back
// through step one instead.
export function inferTransactionTarget(job: {
    openInvoices: { id: string }[];
    openPreauths: { id: string }[];
}, mayRaiseInvoice: boolean): CreateTransactionTarget | undefined {
    if (job.openPreauths.length > 0) { return undefined; }

    if (job.openInvoices.length === 0) {
        return mayRaiseInvoice ? undefined : { kind: 'deposit' };
    }

    return job.openInvoices.length === 1
        ? { kind: 'invoice', invoiceId: job.openInvoices[0].id }
        : undefined;
}

// Requirement: an untouched editor must serialize to nothing. Quill's
// document always ends in a trailing-newline op, so a blank editor still
// produces `{"ops":[{"insert":"\n"}]}` rather than an empty string, which
// would give every recorded cash payment an expandable remarks row with
// nothing in it.
export function serializeRemarks(remarks: Delta): string | undefined {
    return isDeltaBlank(remarks) ? undefined : JSON.stringify(remarks);
}

export function previousStep(
    step: CreateTransactionStep,
    target: CreateTransactionTarget | undefined,
    askedForInvoiceEvents: boolean,
): CreateTransactionStep {
    if (step === 'events') { return 'targets'; }

    if (step === 'amount' && target?.kind === 'newInvoice' && askedForInvoiceEvents) {
        return 'events';
    }

    const index = CREATE_TRANSACTION_STEPS.indexOf(step);

    return index > 0 ? CREATE_TRANSACTION_STEPS[index - 1] : step;
}

// Requirement: matched by config KEY, not by label — every zone that offers
// cash or check at all defines it under `payment-type.cash` /
// `payment-type.check`, while the label is free per-zone text. Choosing a
// method on the grid commits to that zone's type outright; nothing asks again.
export const PAYMENT_TYPE_KEYS: Partial<Record<CreateTransactionMethod, string>> = {
    cash: 'payment-type.cash',
    check: 'payment-type.check',
    creditCard: 'payment-type.credit-card',
    masterCard: 'payment-type.master-card',
    visa: 'payment-type.visa',
    amex: 'payment-type.amex',
    debit: 'payment-type.debit',
    squareCheckout: 'payment-type.square-checkout',
};

export const CARD_PAYMENT_METHODS: CreateTransactionMethod[] = [
    'creditCard', 'visa', 'masterCard', 'amex', 'debit',
];

// Requirement: no card price is quoted against any of these methods. Money
// recorded here was charged by something outside this BlueSage location, so
// its differential doesn't apply — and the typed figure may already carry a
// surcharge of its own, which quoting on top of would compound.
export const MANUAL_RECORD_METHODS: CreateTransactionMethod[] = [
    'cash', 'check', 'other', 'recordCard', 'squareCheckout', ...CARD_PAYMENT_METHODS,
];

// Requirement: a payment type misconfigured with a provider (`Customer` on
// cash, `Moving Help` on eTransfer, `Paypal` on PayPal in production) is still
// offered as usable. `transactions.create` refuses it, and that refusal is
// what gets the zone's config fixed — hiding the tile would absorb it silently.
export interface ResolvedPaymentType {
    id?: string;
    label: string;
    unavailable?: PaymentTypeUnavailable;
}

export function resolvePaymentTypeForMethod(
    paymentTypes: PaymentTypeConfig[],
    method: CreateTransactionMethod,
    fallbackLabel: string,
): ResolvedPaymentType | undefined {
    const key = PAYMENT_TYPE_KEYS[method];
    if (!key) { return undefined; }

    const config = paymentTypes.find((type) => type.key === key);
    if (!config) { return { label: fallbackLabel, unavailable: 'missing' }; }

    const label = config.name || fallbackLabel;

    if (!config.enabled) { return { label, unavailable: 'disabled' }; }

    return { id: config.id, label };
}

export function usablePaymentTypes(
    paymentTypes: PaymentTypeConfig[],
): PaymentTypeConfig[] {
    return paymentTypes.filter((type) => type.enabled && !type.provider);
}

const GRID_PAYMENT_TYPE_KEYS = Object.values(PAYMENT_TYPE_KEYS);

export function otherPaymentTypes(
    paymentTypes: PaymentTypeConfig[],
): PaymentTypeConfig[] {
    return usablePaymentTypes(paymentTypes)
        .filter((type) => !GRID_PAYMENT_TYPE_KEYS.includes(type.key));
}
